import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Search, Loader2, UserPlus, Mail, Phone, MapPin, Briefcase,
  Linkedin, ExternalLink, CheckCircle2, Copy, XCircle, Zap,
  Upload, FileSpreadsheet, Download, Users, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Single Contact Enrichment ───
function SingleEnrich() {
  const navigate = useNavigate();
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingApollo, setLoadingApollo] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [showApolloPrompt, setShowApolloPrompt] = useState(false);
  const [editData, setEditData] = useState(null);

  const applyResult = (best, url) => {
    setResult(best);
    setEditData({
      first_name: best.first_name || '',
      last_name: best.last_name || '',
      work_email: best.emails?.[0] || '',
      phone: best.phones?.[0] || '',
      title: best.title || '',
      company_name: best.company || '',
      linkedin_url: best.linkedin_url || url,
    });
  };

  const mergeResults = (existing, apollo) => {
    const merged = { ...existing };
    if (!merged.first_name && apollo.first_name) merged.first_name = apollo.first_name;
    if (!merged.last_name && apollo.last_name) merged.last_name = apollo.last_name;
    if (!merged.title && apollo.title) merged.title = apollo.title;
    if (!merged.company && apollo.company) merged.company = apollo.company;
    if (!merged.city && apollo.city) merged.city = apollo.city;
    if (!merged.state && apollo.state) merged.state = apollo.state;
    if (apollo.photo_url) merged.photo_url = apollo.photo_url;
    if (apollo.headline && !merged.headline) merged.headline = apollo.headline;

    const seenE = new Set((merged.emails || []).map(e => e.toLowerCase()));
    for (const e of apollo.emails || []) {
      if (e && !seenE.has(e.toLowerCase())) {
        merged.emails.push(e);
        seenE.add(e.toLowerCase());
      }
    }
    const seenP = new Set(merged.phones || []);
    for (const p of apollo.phones || []) {
      if (p && !seenP.has(p)) {
        merged.phones.push(p);
        seenP.add(p);
      }
    }
    for (const s of apollo.sources || []) {
      if (!merged.sources.includes(s)) merged.sources.push(s);
    }
    return merged;
  };

  const handleFetch = async () => {
    const url = linkedinUrl.trim();
    if (!url) { toast.error('Please enter a LinkedIn profile URL'); return; }
    if (!url.includes('linkedin.com/in/')) {
      toast.error('Please enter a valid LinkedIn profile URL (e.g. linkedin.com/in/username)');
      return;
    }

    setLoading(true);
    setResult(null);
    setError('');
    setAdded(false);
    setEditData(null);
    setShowApolloPrompt(false);

    try {
      const res = await axios.post(`${API}/api/enrich/linkedin`, { linkedin_url: url }, { withCredentials: true });
      if (res.data.status === 'no_data') {
        setShowApolloPrompt(true);
      } else {
        const best = res.data.best;
        const hasContact = (best.emails?.length > 0) || (best.phones?.length > 0);
        applyResult(best, url);
        if (!hasContact) setShowApolloPrompt(true);
      }
    } catch (e) {
      setShowApolloPrompt(true);
    }
    setLoading(false);
  };

  const handleApolloEnrich = async () => {
    const url = linkedinUrl.trim();
    setLoadingApollo(true);
    setShowApolloPrompt(false);

    try {
      const res = await axios.post(`${API}/api/enrich/apollo`, { linkedin_url: url }, { withCredentials: true });
      if (res.data.status === 'no_data') {
        if (!result) setError('No contact data found from any source for this profile.');
        else toast.info('Apollo did not find additional data');
      } else {
        const apolloBest = res.data.best;
        if (result) {
          const merged = mergeResults(result, apolloBest);
          applyResult(merged, url);
          toast.success('Apollo data merged!');
        } else {
          applyResult(apolloBest, url);
          toast.success('Contact found via Apollo!');
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Apollo enrichment failed';
      toast.error(detail);
      if (!result) setError(detail);
    }
    setLoadingApollo(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) handleFetch();
  };

  const handleAddAsLead = async () => {
    if (!editData) return;
    if (!editData.first_name || !editData.last_name) {
      toast.error('First name and last name are required');
      return;
    }

    setAdding(true);
    try {
      const payload = {
        first_name: editData.first_name,
        last_name: editData.last_name,
        work_email: editData.work_email || undefined,
        phone: editData.phone || undefined,
        title: editData.title || undefined,
        company_name: editData.company_name || undefined,
        linkedin_url: editData.linkedin_url || undefined,
        source: 'linkedin_enrich',
        status: 'new',
      };
      const res = await axios.post(`${API}/api/leads`, payload, { withCredentials: true });
      toast.success(`${editData.first_name} ${editData.last_name} added as lead!`);
      setAdded(true);
      if (res.data?.id) setTimeout(() => navigate(`/admin/leads/${res.data.id}`), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create lead');
    }
    setAdding(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <>
      {/* Search Box */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                type="url"
                placeholder="https://www.linkedin.com/in/johndoe"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-12 text-base px-4"
                disabled={loading || loadingApollo}
              />
            </div>
            <Button
              onClick={handleFetch}
              disabled={loading || loadingApollo || !linkedinUrl.trim()}
              className="h-12 px-6 gap-2 text-base bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {loading ? 'Searching...' : 'Fetch'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
          <p className="text-gray-700 font-medium">Searching enrichment databases...</p>
          <p className="text-xs text-gray-400 mt-1">This may take a few seconds</p>
        </div>
      )}

      {loadingApollo && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-50 mb-4">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
          <p className="text-gray-700 font-medium">Searching Apollo.io...</p>
          <p className="text-xs text-gray-400 mt-1">Checking Apollo's 275M+ contact database</p>
        </div>
      )}

      {showApolloPrompt && !loading && !loadingApollo && (
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 mb-4">
          <CardContent className="py-5">
            <div className="flex items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Zap className="w-6 h-6 text-orange-500" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">
                  {result ? 'Missing email or phone?' : 'No results from primary sources'}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Try Apollo.io's database of 275M+ contacts for additional data.
                </p>
              </div>
              <Button onClick={handleApolloEnrich} className="gap-2 bg-orange-500 hover:bg-orange-600 text-white shrink-0">
                <Zap className="w-4 h-4" />
                Try Apollo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && !result && !loading && !loadingApollo && !showApolloPrompt && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-6 text-center">
            <XCircle className="w-10 h-10 text-red-300 mx-auto mb-3" />
            <p className="text-red-700 font-medium">{error}</p>
          </CardContent>
        </Card>
      )}

      {result && !loading && !loadingApollo && (
        <div className="space-y-4">
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Contact Found
                </CardTitle>
                <div className="flex gap-1 flex-wrap">
                  {result.sources.map((s) => (
                    <Badge key={s} variant="outline" className="text-xs bg-white">{s}</Badge>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                {result.photo_url && (
                  <img src={result.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{result.first_name} {result.last_name}</h2>
                  {result.title && <p className="text-sm text-gray-500 mt-0.5">{result.title}</p>}
                  {result.headline && !result.title && <p className="text-sm text-gray-500 mt-0.5">{result.headline}</p>}
                  {result.company && (
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600">
                      <Briefcase className="w-3.5 h-3.5 text-gray-400" />{result.company}
                    </div>
                  )}
                  {(result.city || result.state) && (
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />{[result.city, result.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {result.emails.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 block">Emails</Label>
                  <div className="space-y-1.5">
                    {result.emails.map((email, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                        <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                        <a href={`mailto:${email}`} className="text-sm font-medium text-gray-800 hover:text-blue-600 flex-1 truncate">{email}</a>
                        <button onClick={() => copyToClipboard(email)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.phones.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 block">Phones</Label>
                  <div className="space-y-1.5">
                    {result.phones.map((phone, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                        <Phone className="w-4 h-4 text-green-400 shrink-0" />
                        <a href={`tel:${phone}`} className="text-sm font-medium text-gray-800 hover:text-green-600 flex-1">{phone}</a>
                        <button onClick={() => copyToClipboard(phone)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.emails.length === 0 && result.phones.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                  Name and company found but no email or phone. Try Apollo above for contact data.
                </div>
              )}

              {result.linkedin_url && (
                <a href={result.linkedin_url.startsWith('http') ? result.linkedin_url : `https://${result.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <Linkedin className="w-4 h-4" />View LinkedIn Profile<ExternalLink className="w-3 h-3" />
                </a>
              )}
            </CardContent>
          </Card>

          {editData && !added && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-indigo-500" />Add as Lead
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">First Name *</Label>
                    <Input value={editData.first_name} onChange={(e) => setEditData(d => ({ ...d, first_name: e.target.value }))} placeholder="First name" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Last Name *</Label>
                    <Input value={editData.last_name} onChange={(e) => setEditData(d => ({ ...d, last_name: e.target.value }))} placeholder="Last name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Email</Label>
                    <Input value={editData.work_email} onChange={(e) => setEditData(d => ({ ...d, work_email: e.target.value }))} placeholder="Email" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Phone</Label>
                    <Input value={editData.phone} onChange={(e) => setEditData(d => ({ ...d, phone: e.target.value }))} placeholder="Phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Title</Label>
                    <Input value={editData.title} onChange={(e) => setEditData(d => ({ ...d, title: e.target.value }))} placeholder="Job title" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Company</Label>
                    <Input value={editData.company_name} onChange={(e) => setEditData(d => ({ ...d, company_name: e.target.value }))} placeholder="Company name" />
                  </div>
                </div>
                <Button onClick={handleAddAsLead} disabled={adding} className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700 mt-2">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  {adding ? 'Adding...' : 'Add as Lead'}
                </Button>
              </CardContent>
            </Card>
          )}

          {added && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-6 text-center">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-green-800 font-bold text-lg">Lead Created!</p>
                <p className="text-green-600 text-sm mt-1">Redirecting to lead detail...</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

// ─── Bulk Enrich (CSV Upload) ───
function BulkEnrich() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (!f.name.toLowerCase().endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(f);
      setResults(null);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a CSV file first'); return; }

    setUploading(true);
    setError('');
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/api/enrich/bulk`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResults(res.data);
      toast.success(`Enrichment complete: ${res.data.total_enriched} of ${res.data.total_input} contacts found`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Bulk enrichment failed';
      setError(detail);
      toast.error(detail);
    }
    setUploading(false);
  };

  const downloadResults = () => {
    if (!results?.results?.length) return;

    const rows = [['LinkedIn URL', 'Match Status', 'Emails', 'Phones']];
    for (const r of results.results) {
      rows.push([
        r.linkedin_url,
        r.match_status,
        r.emails.join('; '),
        r.phones.join('; '),
      ]);
    }

    const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enriched_${file?.name || 'results'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <>
      {/* Upload Area */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-blue-300 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium mb-1">
              {file ? file.name : 'Upload a CSV file with LinkedIn URLs'}
            </p>
            <p className="text-xs text-gray-400 mb-4">
              CSV must have a column with "linkedin" in the name (e.g. LinkedinProfileURL, linkedin_url)
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                {file ? 'Change File' : 'Select CSV'}
              </Button>
              {file && (
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  {uploading ? 'Enriching...' : 'Enrich Now'}
                </Button>
              )}
            </div>
            {file && (
              <p className="text-xs text-gray-400 mt-3">
                Free enrichment via 100M+ contact database
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {uploading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
          <p className="text-gray-700 font-medium">Enriching contacts...</p>
          <p className="text-xs text-gray-400 mt-1">This may take a minute for large files</p>
        </div>
      )}

      {/* Error */}
      {error && !uploading && (
        <Card className="border-red-200 bg-red-50 mb-6">
          <CardContent className="py-5">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !uploading && (
        <div className="space-y-4">
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{results.total_input}</p>
                <p className="text-xs text-gray-500 mt-1">URLs Submitted</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-green-600">{results.total_enriched}</p>
                <p className="text-xs text-gray-500 mt-1">Contacts Found</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-400">{results.total_input - results.total_enriched}</p>
                <p className="text-xs text-gray-500 mt-1">Not Found</p>
              </CardContent>
            </Card>
          </div>

          {/* Download Button */}
          <div className="flex justify-end">
            <Button onClick={downloadResults} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Download Enriched CSV
            </Button>
          </div>

          {/* Results Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">LinkedIn URL</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Emails</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Phones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((r, i) => {
                      const slug = r.linkedin_url.split('/in/')[1]?.replace(/\/$/, '') || r.linkedin_url;
                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-2.5 px-4">
                            <a href={r.linkedin_url.startsWith('http') ? r.linkedin_url : `https://${r.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1.5 max-w-[200px]">
                              <Linkedin className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{slug}</span>
                            </a>
                          </td>
                          <td className="py-2.5 px-4">
                            {r.has_contact ? (
                              <Badge className="bg-green-100 text-green-700 border-0 text-xs">{r.match_status || 'found'}</Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400 text-xs">{r.match_status || 'not found'}</Badge>
                            )}
                          </td>
                          <td className="py-2.5 px-4">
                            {r.emails.length > 0 ? (
                              <div className="space-y-1">
                                {r.emails.map((e, j) => (
                                  <div key={j} className="flex items-center gap-1.5">
                                    <Mail className="w-3 h-3 text-blue-400 shrink-0" />
                                    <span className="truncate max-w-[180px]">{e}</span>
                                    <button onClick={() => copyToClipboard(e)} className="p-0.5 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-600"><Copy className="w-3 h-3" /></button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-4">
                            {r.phones.length > 0 ? (
                              <div className="space-y-1">
                                {r.phones.map((p, j) => (
                                  <div key={j} className="flex items-center gap-1.5">
                                    <Phone className="w-3 h-3 text-green-400 shrink-0" />
                                    <span>{p}</span>
                                    <button onClick={() => copyToClipboard(p)} className="p-0.5 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-600"><Copy className="w-3 h-3" /></button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

// ─── Main Page with Tabs ───
export default function EnrichPage() {
  const [tab, setTab] = useState('single');

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Enrich Contact</h1>
          <p className="text-sm text-gray-500">
            {tab === 'single'
              ? 'Enter a LinkedIn profile URL to find contact details like email and phone number.'
              : 'Upload a CSV file with LinkedIn URLs to enrich contacts in bulk.'}
          </p>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('single')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'single'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Search className="w-4 h-4" />
          Single Contact
        </button>
        <button
          onClick={() => setTab('bulk')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'bulk'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Bulk Enrich
        </button>
      </div>

      {tab === 'single' ? <SingleEnrich /> : <BulkEnrich />}
    </div>
  );
}
